import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Send, Loader2, Leaf, Trash2, Plus, MessageSquare,
  CheckCircle2, Circle, Pencil, X, Check, Copy,
  PanelRightOpen, PanelRightClose, // For logs panel toggle (currently hidden)
  Share2,
} from 'lucide-react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MarketplaceShell from './MarketplaceShell';
import { audienceSummary } from './audienceLabel';
import { labelForUseCase } from './useCaseMeta';
import MarketplaceErrorScreen, { errorScreenPropsFor } from './MarketplaceErrorScreen';
import { startChat, consumeChatQuery, getChatContext } from './marketplaceApi';
import InsightToggleView from '../digitalTwin/insight/InsightToggleView';
import ChatShareModal from '../digitalTwin/components/ChatShareModal';
import { hasRenderableVisual } from '../digitalTwin/insight/util';
import type { VisualPayload } from '../digitalTwin/insight/types';
import PlanReviewCard from '../../components/chat/PlanReviewCard';
import {
  APPROVE_PLAN_MESSAGE,
  PLAN_REVIEW_FORMAT,
  planEtaRunCopy,
  resolveRunEta,
} from '../../components/chat/planReview';
import type { PlanReviewPayload } from '../../components/chat/planReview';

const API_URL = import.meta.env.VITE_API_URL || '';
const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

// NOTE: this screen has no segment picker. The audience is the study's purchased
// profiles: the frontend POSTs /marketplace/studies/{id}/chat/start with an empty
// body, and the backend (routes_fastapi/breakfast_marketplace.py) resolves those
// profiles to persona_ids and binds the session to the FLAT breakfast segment
// (BREAKFAST_CHAT_SEGMENT_ID = 744) server-side, scoping the chat via
// agent_context.persona_ids.
//
// A vestigial AVAILABLE_SEGMENTS list + per-segment id consts used to live here from
// an older design that did have a dropdown. Nothing referenced them (selectedSegmentId
// is hard-coded to 0 and setSelectedSegmentId was never called), so they were removed:
// they only invited "fixes" to a list that could never render.

// ── Polling cadence (#80): responsive at first, then back off for long tasks ──
const STATUS_POLL_BASE_MS = 1000;
const STATUS_POLL_MAX_MS = 4000;
const LOG_POLL_BASE_MS = 1500;
const LOG_POLL_MAX_MS = 5000;
const LOG_POLL_INITIAL_DELAY_MS = 800;
const POLL_BACKOFF_AFTER_ITERATIONS = 30; // keep the fast cadence for the first ~30 polls
const MAX_POLL_FAILURES = 8; // consecutive status-poll failures before giving up
// A task whose status hasn't CHANGED in this long is treated as lost (evicted
// worker etc.). Keyed on change, not total wall-clock: legitimate runs can exceed
// any total ceiling (a 19-min run is normal for a large plan), but they keep
// emitting progress — except the final report-writing phase, which is silent for
// several minutes, so this window must stay well above that.
const MAX_POLL_STALL_MS = 15 * 60 * 1000;
const POLL_BACKOFF_STEP_MS = 500;

// Honest time expectation for long-running agent queries (#28/#62)
// Shown BEFORE the plan starts running (analyzing/planning) — short, no ETA yet.
const CHAT_PREPARING_COPY = 'Getting your focus group ready…';
// Shown once EXECUTION has started (the long wait) — set an honest expectation and
// reassure the user they can walk away; the answer is here when they return.
const CHAT_ETA_COPY = 'This usually takes about 10–15 minutes.';
// The SAME expectation, set BEFORE the user commits their first query. This used to
// promise "a couple of minutes" while the in-flight panel said 10–15 — the user only
// learned the real number once they were locked in (#21/#28).
const CHAT_UPFRONT_ETA_COPY = 'Answers usually take about 10–15 minutes.';
// The plan's steps all finish well before the answer does — the final synthesis
// (actually writing the report) emits no progress events, so the bar parked at 95%
// with "4/4 done" for ~4.5 min and read as HUNG. Name that phase instead of freezing.
const CHAT_WRITING_COPY = 'Writing your report';
const CHAT_WRITING_HINT = 'All twins have answered. Composing the final report — this is the last step.';
// Shown when status polling gives up on a stalled task. The run may still finish
// server-side (results are saved to the conversation), so point the user at a
// refresh rather than telling them to re-ask — a retry costs another query.
const CHAT_STALLED_COPY =
  "This is taking longer than expected. If the run finishes in the background, its result is saved — refresh the page in a few minutes to check before asking again.";
const CHAT_LEAVE_HINT = 'You can safely leave or close this page — your answer will be waiting here when you come back.';
const ELAPSED_TICK_MS = 1000;

// How close to the bottom the user must already be for the pane to keep following
// new messages. Beyond it they are reading history and we leave them there.
const CHAT_STICKY_BOTTOM_PX = 120;

const nextPollDelay = (iteration: number, baseMs: number, maxMs: number): number =>
  iteration < POLL_BACKOFF_AFTER_ITERATIONS
    ? baseMs
    : Math.min(maxMs, baseMs + (iteration - POLL_BACKOFF_AFTER_ITERATIONS + 1) * POLL_BACKOFF_STEP_MS);

const formatElapsed = (totalSeconds: number): string =>
  totalSeconds < 60
    ? `${totalSeconds}s`
    : `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s`;

// ── Types ────────────────────────────────────────────────────────────────

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  personas_cited?: string[];
  format?: string;
  visual?: VisualPayload; // LLM-authored HTML visual; rendered via the shared sandboxed HtmlVisual
  plan_review?: PlanReviewPayload; // format 'plan_review': the editable plan card (content is a markdown fallback)
  progress_snapshot?: StructuredProgress; // Saved progress for display in chat history
}

interface ChatSession {
  id: number;
  title: string | null;
  message_count: number;
  created_at: string | null;
  updated_at: string | null;
}

// Progress tracking types
interface StageInfo {
  stage: string;
  label: string;
  status: 'pending' | 'active' | 'completed';
  group?: 'planning' | 'output';  // Group for visual organization
}

interface PlanStepProgress {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'skipped';
}

interface StructuredProgress {
  current_stage: string;
  stages?: StageInfo[]; // absent on client-created placeholders (sending/resuming)
  plan_steps?: PlanStepProgress[];
  plan_version?: number;
  message: string;
  // Live ETA the backend may attach mid-run; preferred over the approved plan's ETA.
  eta_minutes_min?: number;
  eta_minutes_max?: number;
}

interface TaskStatusResp {
  task_id: string;
  state: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';
  progress?: number;
  message?: string;
  structured_progress?: StructuredProgress;
  result?: {
    response: string;
    format: string;
    personas_cited: string[];
    visual?: VisualPayload;
    plan_review?: PlanReviewPayload;
  };
  error?: string;
}

interface LogEntry {
  id: number;
  text: string;
  agent: string;
  label: string;
  isHeader: boolean;
  isSeparator: boolean;
}

// ── Time-based progress: NEVER goes backwards ──

// Calculate progress with configurable duration
// Fast tasks (analyzing, executing): 60 seconds to ~80%
// Slow tasks (planning, output): 4 minutes (240 seconds) to ~95%
const calculateProgress = (
  startTime: number,
  maxProgress: number = 80,
  durationMs: number = 60000
): number => {
  const elapsed = Date.now() - startTime;

  // Use easing function for natural feel
  const t = Math.min(elapsed / durationMs, 1);

  // Ease-out curve: fast start, slows down as approaching max
  // Formula: 1 - (1-t)^2 gives nice ease-out
  const eased = 1 - Math.pow(1 - t, 2);

  return eased * maxProgress;
};

// Duration constants
const FAST_DURATION = 60000;   // 60 seconds for fast stages
const SLOW_DURATION = 240000;  // 4 minutes for planning/output

// ── Time-based progress bar component ──
const TimeBasedProgressBar: React.FC<{
  taskId: string;
  startTime: number | undefined;
  isCompleted: boolean;
}> = ({ taskId, startTime, isCompleted }) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const maxReachedRef = useRef(0); // CRITICAL: Track max progress reached - never go below this
  const effectiveStartTimeRef = useRef<number | null>(null); // Capture start time on first active render

  useEffect(() => {
    if (isCompleted) {
      // Animate to 100% smoothly
      const animateTo100 = () => {
        setDisplayProgress(prev => {
          if (prev >= 99.5) {
            maxReachedRef.current = 100;
            return 100;
          }
          const next = prev + (100 - prev) * 0.2;
          maxReachedRef.current = Math.max(maxReachedRef.current, next);
          return next;
        });
      };
      const interval = setInterval(animateTo100, 50);
      setTimeout(() => {
        clearInterval(interval);
        setDisplayProgress(100);
        maxReachedRef.current = 100;
      }, 500);
      return () => clearInterval(interval);
    }

    // Use provided startTime OR capture current time on first active render
    // This fixes the bug where startTime is undefined due to React async state updates
    if (!effectiveStartTimeRef.current) {
      effectiveStartTimeRef.current = startTime || Date.now();
    }
    const effectiveStartTime = effectiveStartTimeRef.current;

    // Use slow duration (4 min) for final_output, fast (60s) for other tasks
    const isSlowTask = taskId === 'final_output';
    const duration = isSlowTask ? SLOW_DURATION : FAST_DURATION;
    const hashCode = taskId.split('').reduce((a, b) => ((a << 5) - a) + b.charCodeAt(0), 0);
    const maxProgress = isSlowTask ? 90 + (Math.abs(hashCode) % 8) : 75 + (Math.abs(hashCode) % 10); // 90-98% or 75-85%

    const interval = setInterval(() => {
      const targetProgress = calculateProgress(effectiveStartTime, maxProgress, duration);

      // CRITICAL: Never go below the maximum we've reached
      const newProgress = Math.max(maxReachedRef.current, targetProgress);
      maxReachedRef.current = newProgress;

      // Smooth transition towards target
      setDisplayProgress(prev => {
        const diff = newProgress - prev;
        if (Math.abs(diff) < 0.3) return newProgress;
        // Move 20% of the way each tick for smooth animation
        return prev + diff * 0.2;
      });
    }, 100); // Update every 100ms for smooth animation

    return () => clearInterval(interval);
  }, [startTime, isCompleted, taskId]);

  const showProgress = Math.round(displayProgress);

  return (
    <div className="mt-2">
      <div className="h-1.5 bg-[#07E5D1]/20 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#07E5D1] rounded-full transition-all duration-300 ease-out"
          style={{ width: `${showProgress}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-1">
        <p className="text-[10px] text-[#07E5D1] flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          {showProgress < 30 ? 'Starting...' : showProgress < 60 ? 'Processing...' : 'Almost there...'}
        </p>
        <span className="text-[10px] text-gray-400">{showProgress}%</span>
      </div>
    </div>
  );
};

// ── Phased progress model ─────────────────────────────────────────────
// The run has four visible phases. Execution (the per-persona simulation) is the
// plan-step accordion above; the three POST-execution phases used to emit no
// progress, so the bar parked at ~95% for many minutes ("false done"). Each phase
// owns a % band and a realistic ETA, so the bar keeps moving and the user sees
// which step is running and how long it takes.
interface PhaseMeta { floor: number; ceil: number; label: string; eta: string; durationMs: number; }
type PhaseKey = 'execution' | 'generating' | 'validating_output' | 'visualizing';
const PHASES: Record<PhaseKey, PhaseMeta> = {
  execution:         { floor: 0,  ceil: 65,  label: 'Running the simulation',  eta: '',          durationMs: 240000 },
  generating:        { floor: 65, ceil: 80,  label: 'Generating your answer',  eta: '3–5 min',   durationMs: 240000 },
  validating_output: { floor: 80, ceil: 85,  label: 'Checking accuracy',       eta: '2–3 min',   durationMs: 150000 },
  visualizing:       { floor: 85, ceil: 100, label: 'Designing the visual',    eta: '3–5 min',   durationMs: 240000 },
};
// Map every backend stage onto a phase. Anything up to and including the executing
// work is the "execution" phase; the three named phases follow.
const STAGE_TO_PHASE: Record<string, PhaseKey> = {
  analyzing: 'execution', planning: 'execution', replanning: 'execution',
  validating_plan: 'execution', executing: 'execution', extracting: 'execution',
  generating: 'generating',
  validating_output: 'validating_output',
  visualizing: 'visualizing',
};
const phaseForStage = (stage: string | undefined): PhaseKey =>
  (stage && STAGE_TO_PHASE[stage]) || 'execution';

// Pure: the overall % for a phase given how long it's been running, eased toward
// (but never past) the phase ceiling. Kept pure so it can be unit-tested.
export const phasedPercent = (phase: PhaseKey, elapsedInPhaseMs: number): number => {
  const p = PHASES[phase];
  const t = Math.min(Math.max(elapsedInPhaseMs, 0) / p.durationMs, 1);
  const eased = 1 - Math.pow(1 - t, 2);
  // ease toward 92% of the band so it doesn't visually "complete" before the phase does
  return p.floor + (p.ceil - p.floor) * eased * 0.92;
};

// Overall phased bar for the post-execution phases (generating → validating → visual).
const PhasedProgressBar: React.FC<{ stage: string | undefined; isCompleted: boolean }> = ({ stage, isCompleted }) => {
  const [pct, setPct] = useState(0);
  const phase = phaseForStage(stage);
  // A fresh instance per phase (via key={phase} at the call site): stamp the phase
  // start on mount and seed the monotonic floor at THIS phase's band. This is why we
  // don't thread `phaseStart` down from a parent effect — that effect lagged the
  // render, so the first tick after a phase change read the PREVIOUS phase's start
  // (a huge elapsed → t=1 → the band ceiling) and maxRef then froze it there.
  const [start] = useState(() => Date.now());
  const maxRef = useRef(PHASES[phase].floor);
  useEffect(() => {
    if (isCompleted) { setPct(100); maxRef.current = 100; return; }
    const tick = () => {
      const target = phasedPercent(phase, Date.now() - start);
      maxRef.current = Math.max(maxRef.current, target); // never go backwards
      setPct(maxRef.current);
    };
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [phase, start, isCompleted]);
  const meta = PHASES[phase];
  const shown = Math.round(isCompleted ? 100 : pct);
  return (
    <div className="mt-2">
      <div className="h-1.5 bg-[#07E5D1]/20 rounded-full overflow-hidden">
        <div className="h-full bg-[#07E5D1] rounded-full transition-all duration-300 ease-out" style={{ width: `${shown}%` }} />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[11px] text-[#374151] font-medium">{meta.label}{meta.eta ? ` · ~${meta.eta}` : ''}</span>
        <span className="text-[11px] text-gray-400">{shown}%</span>
      </div>
    </div>
  );
};

// ── Collapsible Progress Accordion for chat history ──
// Only shows the execution plan steps (the actual work done), not static stages
const ProgressAccordion: React.FC<{ progress: StructuredProgress }> = ({ progress }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const planSteps = progress.plan_steps || [];

  // Don't render if no plan steps
  if (planSteps.length === 0) return null;

  return (
    <div className="mt-3 border border-black/10 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full min-h-[44px] md:min-h-0 px-4 py-2.5 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-[#07E5D1]" />
          <span className="text-xs font-medium text-gray-700">
            Completed {planSteps.length} tasks
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400">View execution plan</span>
          {isExpanded ? (
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 py-3 bg-white border-t border-black/10">
          <ul className="space-y-1.5">
            {planSteps.map((step, idx) => (
              <li key={`${step.id}-${idx}`} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#07E5D1] flex-shrink-0 mt-0.5" />
                <span className="text-xs text-gray-500 line-through">{step.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const AGENT_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  ORCHESTRATOR:      { bg: 'bg-[#E8FCFA]',  text: 'text-[#0B7C74]',   dot: 'bg-[#07E5D1]' },
  QUERY_ANALYZER:    { bg: 'bg-purple-50',  text: 'text-purple-700',  dot: 'bg-purple-500' },
  PLANNING_AGENT:    { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500' },
  EXECUTION_AGENT:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  OUTPUT_ANALYST:    { bg: 'bg-pink-50',    text: 'text-pink-700',    dot: 'bg-pink-500' },
  GLOBAL:            { bg: 'bg-gray-50',    text: 'text-gray-600',    dot: 'bg-gray-400' },
};

function getAgentColor(agent: string) {
  return AGENT_COLORS[agent] || { bg: 'bg-gray-50', text: 'text-gray-600', dot: 'bg-gray-400' };
}

let _logId = 0;
function parseLine(raw: string): LogEntry {
  _logId += 1;
  const trimmed = raw.trim();
  if (/^[═─]{10,}/.test(trimmed)) {
    return { id: _logId, text: '', agent: '', label: '', isHeader: false, isSeparator: true };
  }
  const headerMatch = trimmed.match(/^\[(\d{2}:\d{2}:\d{2}\.\d{3})\]\s+([A-Z_]+)\s+\|\s+(.+)$/);
  if (headerMatch) {
    const [, time, agent, label] = headerMatch;
    return { id: _logId, text: `${time}`, agent, label, isHeader: true, isSeparator: false };
  }
  return { id: _logId, text: trimmed, agent: '', label: '', isHeader: false, isSeparator: false };
}

// ── Markdown components ──────────────────────────────────────────────────

const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-[#1C1C1E] mt-3 mb-1">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold text-[#1C1C1E] mt-2 mb-1">{children}</h4>,
  p: ({ children }) => <p className="text-sm text-[#374151] mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-5 mb-3 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-[#374151] leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#1C1C1E]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#07E5D1] pl-4 py-1 my-3 bg-[#FAFBFC] rounded-r-lg">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-[#FAFBFC] text-[#374151] px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
    ) : (
      <code className="block bg-gray-900 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-3">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-3">{children}</pre>,
  a: ({ href, children }) => (
    <a href={href} className="text-[#07E5D1] hover:underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-[#E5E7EB]" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-[#E5E7EB]">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-[#FAFBFC] border-b border-[#E5E7EB]">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-[#F1F5F9]">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-[#FAFBFC] transition-colors">{children}</tr>,
  th: ({ children }) => (
    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-sm text-[#374151] align-top">{children}</td>
  ),
};

// ── Main Component ───────────────────────────────────────────────────────

const MarketplaceTwinChat: React.FC = () => {
  const navigate = useNavigate();
  const { studyId: studyIdParam } = useParams<{ studyId?: string }>();
  const studyId = Number(studyIdParam);

  // The audience is the marketplace study's selected profiles (an in-memory
  // persona subset), bootstrapped server-side via /chat/start. No segment dropdown.
  const [selectedSegmentId] = useState<number>(0); // retained for unused legacy refs
  const [queriesRemaining, setQueriesRemaining] = useState<number>(0);
  const [queriesAllowed, setQueriesAllowed] = useState<number>(3);
  // A query that is being answered RIGHT NOW is real but not yet committed (the
  // backend defers commit so a clarification-only turn isn't charged). Showing only
  // the committed count meant the header read "3 of 3 queries left" for the whole
  // ~5 min answer — see #66. Surface the in-flight one instead of hiding it.
  const [queriesInFlight, setQueriesInFlight] = useState<number>(0);
  // Execution HIGH-WATER MARK. The backend clears plan_steps when it re-plans, so a
  // naive `plan_steps.length > 0` flips back to false mid-run: the status text walked
  // BACKWARDS ("Structuring a comparative report" → "Identifying the target audience")
  // and the 10–15 min ETA banner vanished (#64). Once execution has started for this
  // turn it stays started; reset happens at the turn boundary, alongside overallPct.
  const [executionLatched, setExecutionLatched] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  // Errors during an active session (a failed Send) — rendered inline above the
  // input, since startError only shows before a session is bootstrapped.
  const [sendError, setSendError] = useState<string | null>(null);

  // Audience context from /chat/start (#27/#35) — older sessions may lack it.
  const [twinCount, setTwinCount] = useState<number | null>(null);
  const [cohortLabels, setCohortLabels] = useState<string[]>([]);

  // // Context filtering toggle (admin only) - undefined means use server config default
  // const [contextFilteringEnabled, setContextFilteringEnabled] = useState<boolean | undefined>(undefined);

  // Session state
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSessionLoading, setIsSessionLoading] = useState(false);

  // Input / loading
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState('Analyzing your question...');
  const [structuredProgress, setStructuredProgress] = useState<StructuredProgress | null>(null);
  // Raise the execution high-water mark the first time the backend reports a plan
  // step for this turn. It is only lowered at a turn boundary (see setOverallPct
  // resets), never by a re-plan clearing plan_steps.
  useEffect(() => {
    if ((structuredProgress?.plan_steps?.length ?? 0) > 0) setExecutionLatched(true);
  }, [structuredProgress]);
  const [stepStartTimes, setStepStartTimes] = useState<Record<string, number>>({});
  const [stageStartTimes, setStageStartTimes] = useState<Record<string, number>>({});
  // PhasedProgressBar stamps its own phase start on mount (it's keyed by phase so a
  // new phase remounts it) — no parent phaseStart state, which used to lag the render.
  const [lastProgressSnapshot, setLastProgressSnapshot] = useState<StructuredProgress | null>(null);
  const progressSnapshotRef = useRef<StructuredProgress | null>(null); // Sync ref for immediate access in SUCCESS handler

  // Overall header % — monotonic across backend re-plans (#64), capped until done.
  const [overallPct, setOverallPct] = useState<number | null>(null);

  // Elapsed-time indicator so long tasks don't look frozen (#28/#62)
  const [queryStartTime, setQueryStartTime] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    if (queryStartTime == null) { setElapsedSeconds(0); return; }
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - queryStartTime) / 1000));
    }, ELAPSED_TICK_MS);
    return () => clearInterval(interval);
  }, [queryStartTime]);

  // Animated dots for loading states (cycles: "" -> "." -> ".." -> "..." -> "")
  const [animatedDots, setAnimatedDots] = useState('');
  useEffect(() => {
    if (!isLoading) { setAnimatedDots(''); return; }
    const interval = setInterval(() => {
      setAnimatedDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 400);
    return () => clearInterval(interval);
  }, [isLoading]);

  const [showLogs, setShowLogs] = useState(true);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const logOffsetRef = useRef(0);
  const logFileRef = useRef<string | null>(null);
  const logScrollRef = useRef<HTMLDivElement>(null);
  const logPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs
  const chatScrollRef = useRef<HTMLDivElement>(null);

  // Scroll chat to bottom
  const scrollToBottom = useCallback(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, []);

  // The pane used to open at scrollTop 0 — on a phone that is a ~500px window onto a
  // ~5,000px history, so a returning user landed on their FIRST question and had to
  // scroll a long way to reach the answer they came back for. Open on the newest
  // message, then only FOLLOW new messages while the user is already parked at the
  // bottom; if they have scrolled up to read, their position is theirs to keep.
  const isAtBottomRef = useRef(true);
  const hasOpenedAtBottomRef = useRef(false);
  const messageCountRef = useRef(0);

  const handleChatScroll = useCallback(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    isAtBottomRef.current =
      el.scrollHeight - el.clientHeight - el.scrollTop <= CHAT_STICKY_BOTTOM_PX;
  }, []);

  useEffect(() => {
    if (isSessionLoading || messages.length === 0) return;
    const grew = messages.length > messageCountRef.current;
    messageCountRef.current = messages.length;
    const opening = !hasOpenedAtBottomRef.current;
    if (!opening && !(grew && isAtBottomRef.current)) return;
    hasOpenedAtBottomRef.current = true;
    // Markdown bubbles and the visual iframe settle a frame after mount, so the
    // scrollHeight read on this tick is short of the final one.
    const frame = requestAnimationFrame(() => requestAnimationFrame(scrollToBottom));
    return () => cancelAnimationFrame(frame);
  }, [messages, isSessionLoading, scrollToBottom]);
  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentTaskIdRef = useRef<string | null>(null);
  const pollFailuresRef = useRef(0);
  const pollStartRef = useRef(0);
  // Stall detection: fingerprint of the last observed status + when it last changed.
  const lastStatusSigRef = useRef('');
  const lastStatusChangeRef = useRef(0);
  // Generation counters: bumping invalidates any in-flight self-scheduling poll
  // loop (covers the race where a fetch resolves after cleanup and would
  // otherwise re-schedule itself) — #80.
  const statusPollGenRef = useRef(0);
  const logPollGenRef = useRef(0);

  // Rename session state
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  // Delete confirmation state
  const [deletingSessionId, setDeletingSessionId] = useState<number | null>(null);

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState<{
    share_token: string;
    share_url: string;
    message_count: number;
    view_count: number;
    fork_count: number;
  } | null>(null);
  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const [isRevokingShare, setIsRevokingShare] = useState(false);

  // Focus input
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Cleanup on unmount — stop ALL polling (status + logs) so nothing keeps
  // hitting the API after navigating away (#80).
  useEffect(() => {
    return () => {
      statusPollGenRef.current += 1;
      logPollGenRef.current += 1;
      currentTaskIdRef.current = null;
      if (pollRef.current) clearTimeout(pollRef.current);
      if (logPollRef.current) clearTimeout(logPollRef.current);
    };
  }, []);


  // ── Session CRUD ─────────────────────────────────────────────────

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions?segment_id=${selectedSegmentId}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch { /* ignore */ }
  }, [selectedSegmentId]);


  // Check if a session has an active task and resume polling if so
  const checkSessionStatus = useCallback(async (sessionId: number) => {
    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${sessionId}/status`, { credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      return data; // { has_active_task: boolean, task_id?: string }
    } catch {
      return null;
    }
  }, []);

  // Store refs to polling functions (defined later) for use in effects
  const pollTaskStatusRef = useRef<((taskId: string) => Promise<void>) | null>(null);
  const startLogPollingRef = useRef<((sessionId: number) => void) | null>(null);

  // Stop any active polling (used when switching sessions)
  const stopAllPolling = useCallback(() => {
    statusPollGenRef.current += 1;
    logPollGenRef.current += 1;
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
    if (logPollRef.current) {
      clearTimeout(logPollRef.current);
      logPollRef.current = null;
    }
    currentTaskIdRef.current = null;
    setIsLoading(false);
    setStructuredProgress(null);
    setProgressMessage('');
    setOverallPct(null);
    setExecutionLatched(false);
    setQueryStartTime(null);
  }, []);

  // Self-scheduling status poll with backoff (#80): fast for the first
  // POLL_BACKOFF_AFTER_ITERATIONS ticks (live-feeling during an active chat),
  // then the delay grows, capped at STATUS_POLL_MAX_MS. Stored in pollRef so
  // existing terminal-state handlers still cancel the pending tick.
  const startTaskPolling = useCallback((taskId: string) => {
    if (pollRef.current) clearTimeout(pollRef.current);
    const gen = ++statusPollGenRef.current;
    currentTaskIdRef.current = taskId;
    pollFailuresRef.current = 0;
    pollStartRef.current = Date.now();
    lastStatusSigRef.current = '';
    lastStatusChangeRef.current = Date.now();
    let iteration = 0;
    const tick = async () => {
      if (gen !== statusPollGenRef.current || currentTaskIdRef.current !== taskId) return;
      iteration += 1;
      await pollTaskStatusRef.current?.(taskId);
      if (gen !== statusPollGenRef.current || currentTaskIdRef.current !== taskId) return;
      pollRef.current = setTimeout(tick, nextPollDelay(iteration, STATUS_POLL_BASE_MS, STATUS_POLL_MAX_MS));
    };
    pollTaskStatusRef.current?.(taskId);
    pollRef.current = setTimeout(tick, STATUS_POLL_BASE_MS);
  }, []);

  const createSession = useCallback(async () => {
    stopAllPolling();

    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ segment_id: selectedSegmentId }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setActiveSessionId(data.id);
      setMessages([]);
      await fetchSessions();
    } catch { /* ignore */ }
  }, [fetchSessions, stopAllPolling, selectedSegmentId]);

  const loadSession = useCallback(async (id: number) => {
    // Stop any existing polling before switching
    stopAllPolling();
    setIsSessionLoading(true);

    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${id}`, { credentials: 'include' });
      if (!res.ok) {
        setIsSessionLoading(false);
        return;
      }
      const data = await res.json();
      setActiveSessionId(id);
      // URL will be updated by the effect that watches activeSessionId
      setMessages(
        (data.messages || []).map((m: any) => ({
          role: m.role,
          content: m.content,
          personas_cited: m.metadata?.personas_cited,
          format: m.metadata?.format,
          visual: m.metadata?.visual,
          plan_review: m.metadata?.plan_review,
          progress_snapshot: m.metadata?.progress_snapshot,
        }))
      );

      // Check if there's an active task and resume polling
      const status = await checkSessionStatus(id);
      if (status?.has_active_task && status.task_id) {
        setIsLoading(true);
        setProgressMessage('Resuming...');
        setStructuredProgress({
          current_stage: 'resuming',
          message: 'Resuming...',
          plan_steps: [],
        });
        setQueryStartTime(Date.now());
        setOverallPct(null);
        setExecutionLatched(false);
        startTaskPolling(status.task_id);
        startLogPollingRef.current?.(id);
      }
    } catch { /* ignore */ }
    setIsSessionLoading(false);
  }, [stopAllPolling, checkSessionStatus, startTaskPolling]);

  // Track if we've initialized from URL to prevent loops
  const hasInitializedFromUrlRef = useRef(false);

  // ONE-TIME: bootstrap the chat session from the marketplace study. /chat/start
  // resolves the study's selected profiles into an in-memory persona audience,
  // creates the hc-v2 session bound to it, and returns the session id + budget.
  useEffect(() => {
    if (hasInitializedFromUrlRef.current || !studyId) return;
    hasInitializedFromUrlRef.current = true;
    (async () => {
      setIsSessionLoading(true);
      try {
        const s = await startChat(studyId);
        setQueriesRemaining(s.queries_remaining);
        setQueriesAllowed(s.queries_allowed);
        setQueriesInFlight(s.queries_in_flight ?? 0);
        // Audience context for the header (#27/#35). cohort_labels is read
        // defensively — the shared ChatStart type doesn't declare it yet and
        // older sessions may not return it.
        setTwinCount(typeof s.twin_count === 'number' ? s.twin_count : null);
        const labels = (s as { cohort_labels?: unknown }).cohort_labels;
        setCohortLabels(Array.isArray(labels) ? labels.filter((l): l is string => typeof l === 'string') : []);
        setActiveSessionId(s.session_id);
        const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${s.session_id}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setMessages((data.messages || []).map((m: any) => ({
            role: m.role, content: m.content,
            personas_cited: m.metadata?.personas_cited,
            format: m.metadata?.format,
            visual: m.metadata?.visual,
            plan_review: m.metadata?.plan_review,
            progress_snapshot: m.metadata?.progress_snapshot,
          })));
        } else {
          // History load failed — don't silently present an existing
          // conversation as a brand-new empty chat.
          setSendError("Couldn't load your earlier messages — you can still ask new questions.");
        }

        // If a query is still running on this session (the user navigated away and
        // came back), resume the live progress so they see the loading indicator
        // and the answer lands here — not just their own question.
        const status = await checkSessionStatus(s.session_id);
        if (status?.has_active_task && status.task_id) {
          const taskId = status.task_id;
          setIsLoading(true);
          setProgressMessage('Resuming…');
          setStructuredProgress({ current_stage: 'resuming', message: 'Resuming…', plan_steps: [] });
          setQueryStartTime(Date.now());
          setOverallPct(null);
          setExecutionLatched(false);
          startTaskPolling(taskId);
          startLogPollingRef.current?.(s.session_id);
        }
      } catch (e) {
        setStartError(e instanceof Error ? e.message : 'Could not start chat');
      }
      setIsSessionLoading(false);
    })();
  }, [studyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteSession = useCallback(async (id: number) => {
    try {
      await fetch(`${API_URL}/healthcare-chat-v2/sessions/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (activeSessionId === id) {
        stopAllPolling();
        setActiveSessionId(null);
        setMessages([]);
        navigate('/marketplace/history', { replace: true });
      }
      await fetchSessions();
    } catch { /* ignore */ }
  }, [activeSessionId, fetchSessions, navigate, stopAllPolling]);

  // ── Rename session handlers ──
  const handleStartRename = useCallback((session: ChatSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSessionId(session.id);
    setEditingTitle(session.title || `Chat ${session.id}`);
  }, []);

  const handleSaveRename = useCallback(async () => {
    if (!editingSessionId || !editingTitle.trim()) {
      setEditingSessionId(null);
      return;
    }
    setIsRenaming(true);
    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${editingSessionId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editingTitle.trim() }),
      });
      if (res.ok) {
        await fetchSessions();
      }
    } catch { /* ignore */ }
    setIsRenaming(false);
    setEditingSessionId(null);
  }, [editingSessionId, editingTitle, fetchSessions]);

  const handleCancelRename = useCallback(() => {
    setEditingSessionId(null);
    setEditingTitle('');
  }, []);

  const handleRenameKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveRename();
    } else if (e.key === 'Escape') {
      handleCancelRename();
    }
  }, [handleSaveRename, handleCancelRename]);

  // ── Delete confirmation handlers ──
  const handleDeleteClick = useCallback((id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingSessionId(id);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deletingSessionId) {
      await deleteSession(deletingSessionId);
      setDeletingSessionId(null);
    }
  }, [deletingSessionId, deleteSession]);

  const handleCancelDelete = useCallback(() => {
    setDeletingSessionId(null);
  }, []);

  // ── Share Functions ─────────────────────────────────────────────────

  const fetchExistingShare = useCallback(async (sessionId: number) => {
    try {
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${sessionId}/share`,
        { credentials: 'include' }
      );
      if (!res.ok) return null;
      const data = await res.json();
      if (data.has_share) {
        return {
          share_token: data.share_token,
          share_url: data.share_url,
          message_count: data.message_count,
          view_count: data.view_count || 0,
          fork_count: data.fork_count || 0,
        };
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const handleShareClick = useCallback(async () => {
    if (!activeSessionId || messages.length === 0) return;

    setIsCreatingShare(true);
    try {
      // First check if there's an existing share
      const existing = await fetchExistingShare(activeSessionId);
      if (existing) {
        setShareData(existing);
        setShowShareModal(true);
        setIsCreatingShare(false);
        return;
      }

      // Create a new share
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${activeSessionId}/share`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ allow_fork: true }),
        }
      );

      if (!res.ok) {
        console.error('Failed to create share link');
        return;
      }

      const data = await res.json();
      setShareData({
        share_token: data.share_token,
        share_url: data.share_url,
        message_count: data.message_count,
        view_count: 0,
        fork_count: 0,
      });
      setShowShareModal(true);
    } catch (err) {
      console.error('Error creating share:', err);
    } finally {
      setIsCreatingShare(false);
    }
  }, [activeSessionId, messages.length, fetchExistingShare]);

  const handleRevokeShare = useCallback(async () => {
    if (!activeSessionId || !shareData) return;

    setIsRevokingShare(true);
    try {
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${activeSessionId}/share/${shareData.share_token}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (res.ok) {
        setShareData(null);
        setShowShareModal(false);
      }
    } catch (err) {
      console.error('Error revoking share:', err);
    } finally {
      setIsRevokingShare(false);
    }
  }, [activeSessionId, shareData]);

  // Clear share data when session changes
  useEffect(() => {
    setShareData(null);
    setShowShareModal(false);
  }, [activeSessionId]);

  const pollLogs = useCallback(async (sessionId: number) => {
    try {
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${sessionId}/logs?offset=${logOffsetRef.current}`,
        { credentials: 'include' },
      );
      if (!res.ok) return;
      const data = await res.json();
      if (data.file && logFileRef.current && data.file !== logFileRef.current) {
        setLogEntries([]);
        _logId = 0;
        logOffsetRef.current = 0;
        logFileRef.current = data.file;
        return;
      }
      logFileRef.current = data.file ?? null;
      if (data.lines && data.lines.length > 0) {
        const newEntries = data.lines.map((ln: string) => parseLine(ln));
        // Bound the (currently-hidden) buffer so a long run can't grow it
        // unboundedly and re-render the whole chat on every poll tick.
        setLogEntries(prev => [...prev, ...newEntries].slice(-300));
      }
      logOffsetRef.current = data.offset;
    } catch {}
  }, []);

  // Self-scheduling log poll with backoff (#80) — same pattern as the status
  // poll: responsive early, then slower, and fully cancellable on unmount.
  const startLogPolling = useCallback((sessionId: number) => {
    if (logPollRef.current) clearTimeout(logPollRef.current);
    const gen = ++logPollGenRef.current;
    setLogEntries([]);
    _logId = 0;
    logOffsetRef.current = 0;
    logFileRef.current = null;
    let iteration = 0;
    const tick = async () => {
      if (gen !== logPollGenRef.current) return;
      iteration += 1;
      await pollLogs(sessionId);
      if (gen !== logPollGenRef.current) return;
      logPollRef.current = setTimeout(tick, nextPollDelay(iteration, LOG_POLL_BASE_MS, LOG_POLL_MAX_MS));
    };
    logPollRef.current = setTimeout(tick, LOG_POLL_INITIAL_DELAY_MS);
  }, [pollLogs]);

  const stopLogPolling = useCallback(() => {
    logPollGenRef.current += 1;
    if (logPollRef.current) {
      clearTimeout(logPollRef.current);
      logPollRef.current = null;
    }
  }, []);

  // ── Task status polling ──────────────────────────────────────────

  const pollTaskStatus = useCallback(async (taskId: string) => {
    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/status/${taskId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('status poll failed');
      const status: TaskStatusResp = await res.json();
      pollFailuresRef.current = 0; // a good poll resets the failure streak

      if (status.state === 'PENDING' || status.state === 'PROGRESS') {
        // Stall detection: a task is treated as lost only when its observable
        // status stops changing for MAX_POLL_STALL_MS — a live long run keeps
        // emitting progress, while an evicted worker (or a task never picked up)
        // goes silent. Total wall-clock is deliberately NOT a criterion.
        const statusSig = `${status.state}|${status.progress ?? ''}|${status.message ?? ''}|${JSON.stringify(status.structured_progress ?? null)}`;
        if (statusSig !== lastStatusSigRef.current) {
          lastStatusSigRef.current = statusSig;
          lastStatusChangeRef.current = Date.now();
        }
        if (lastStatusChangeRef.current && Date.now() - lastStatusChangeRef.current > MAX_POLL_STALL_MS) {
          if (currentTaskIdRef.current === taskId) currentTaskIdRef.current = null;
          if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
          stopLogPolling();
          setIsLoading(false);
          setStructuredProgress(null);
          setQueryStartTime(null);
          setMessages(prev => [...prev, { role: 'assistant', content: CHAT_STALLED_COPY }]);
          return;
        }
        setProgressMessage(status.message || 'Processing...');
        if (status.structured_progress) {
          // Track when steps start running
          const newStepStartTimes: Record<string, number> = { ...stepStartTimes };
          status.structured_progress.plan_steps?.forEach(step => {
            if (step.status === 'running' && !newStepStartTimes[step.id]) {
              newStepStartTimes[step.id] = Date.now();
            }
          });
          // Also track when the output stage starts
          const outputStage = status.structured_progress.stages?.find(s => s.stage === 'output');
          if (outputStage?.status === 'active' && !newStepStartTimes['final_output']) {
            newStepStartTimes['final_output'] = Date.now();
          }
          setStepStartTimes(newStepStartTimes);

          // Track when stages become active
          const newStageStartTimes: Record<string, number> = { ...stageStartTimes };
          status.structured_progress.stages?.forEach(stage => {
            if (stage.status === 'active' && !newStageStartTimes[stage.stage]) {
              newStageStartTimes[stage.stage] = Date.now();
            }
          });
          setStageStartTimes(newStageStartTimes);

          setStructuredProgress(status.structured_progress);
          setLastProgressSnapshot(status.structured_progress);
          progressSnapshotRef.current = status.structured_progress; // Sync update for immediate access in SUCCESS handler

          // Overall header % is monotonic across plan versions (#64): a backend
          // re-plan can clear/replace steps, which would otherwise drop the
          // completed/total ratio and make the % move backwards. The execution
          // phase owns the 0–PHASES.execution.ceil band, so plan-step progress maps
          // into that band — NOT 0–100 — otherwise the badge would climb toward ~95%
          // and then drop to the generating floor (65%) at the phase handoff.
          const planSteps = status.structured_progress.plan_steps || [];
          if (planSteps.length > 0) {
            const done = planSteps.filter(s => s.status === 'completed' || s.status === 'skipped').length;
            const computedPct = Math.round((done / planSteps.length) * PHASES.execution.ceil);
            setOverallPct(prev => Math.max(prev ?? 0, computedPct));
          }
        }
      } else if (status.state === 'SUCCESS' && status.result) {
        // Prevent duplicate processing if multiple in-flight polls return SUCCESS
        // simultaneously (a slow fetch lets the next interval tick overlap it).
        if (currentTaskIdRef.current !== taskId) return;
        currentTaskIdRef.current = null; // Claim the terminal state immediately
        if (pollRef.current) {
          clearTimeout(pollRef.current);
          pollRef.current = null;
        }
        stopLogPolling();
        // Create final progress snapshot with all steps completed (including final output step)
        // Use ref for immediate access - not subject to React state timing issues
        const snapshotToUse = progressSnapshotRef.current || lastProgressSnapshot;
        const finalSnapshot: StructuredProgress | undefined = snapshotToUse ? {
          ...snapshotToUse,
          stages: snapshotToUse.stages?.map(s => ({ ...s, status: 'completed' as const })),
          plan_steps: [
            ...(snapshotToUse.plan_steps?.map(s => ({ ...s, status: 'completed' as const })) || []),
            { id: 'final_output', label: 'Preparing your answer', status: 'completed' as const }
          ],
        } : undefined;

        setMessages(prev => [...prev, {
          role: 'assistant',
          content: status.result!.response,
          personas_cited: status.result!.personas_cited,
          format: status.result!.format,
          visual: status.result!.visual,
          plan_review: status.result!.plan_review,
          progress_snapshot: finalSnapshot,
        }]);
        // Do NOT force-scroll on a new assistant message — preserve the user's position.
        setIsLoading(false);
        setStructuredProgress(null);
        setStepStartTimes({});
        setStageStartTimes({});
        setLastProgressSnapshot(null);
        setOverallPct(null);
        setExecutionLatched(false);
        setQueryStartTime(null);
        progressSnapshotRef.current = null; // Clear the ref too
        await fetchSessions();
        // The query slot is committed/refunded server-side on the completion path
        // (a clarification-only turn does NOT consume a slot). Re-read the true
        // budget instead of trusting the optimistic pre-turn value.
        try {
          const ctx = await getChatContext(studyId);
          setQueriesRemaining(ctx.queries_remaining);
          setQueriesInFlight(ctx.queries_in_flight ?? 0);
        } catch { /* non-fatal: keep the current counter */ }
      } else if (status.state === 'FAILURE') {
        // Same duplicate-guard as SUCCESS: only the first terminal poll commits.
        if (currentTaskIdRef.current !== taskId) return;
        currentTaskIdRef.current = null; // Claim the terminal state immediately
        if (pollRef.current) {
          clearTimeout(pollRef.current);
          pollRef.current = null;
        }
        stopLogPolling();
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Sorry, I encountered an error: ${status.error || 'Unknown error'}. Please try again.`,
        }]);
        // Do NOT force-scroll on a new assistant message — preserve the user's position.
        setIsLoading(false);
        setStructuredProgress(null);
        setStepStartTimes({});
        setStageStartTimes({});
        setLastProgressSnapshot(null);
        setOverallPct(null);
        setExecutionLatched(false);
        setQueryStartTime(null);
        progressSnapshotRef.current = null; // Clear the ref too
        // An errored turn keeps the slot open (no charge); re-read the true budget.
        try {
          const ctx = await getChatContext(studyId);
          setQueriesRemaining(ctx.queries_remaining);
          setQueriesInFlight(ctx.queries_in_flight ?? 0);
        } catch { /* non-fatal */ }
      }
    } catch {
      // Tolerate transient poll errors, but don't spin forever: after a run of
      // consecutive failures, stop and tell the user.
      pollFailuresRef.current += 1;
      if (pollFailuresRef.current >= MAX_POLL_FAILURES) {
        if (currentTaskIdRef.current === taskId) currentTaskIdRef.current = null;
        if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
        stopLogPolling();
        pollFailuresRef.current = 0;
        setIsLoading(false);
        setStructuredProgress(null);
        setQueryStartTime(null);
        setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I lost connection while preparing your answer. Please try asking again." }]);
      }
    }
  }, [activeSessionId, fetchSessions, stepStartTimes, stageStartTimes, lastProgressSnapshot, stopLogPolling, studyId]);

  // Set refs for polling functions (used by effects that run before these are defined)
  useEffect(() => {
    pollTaskStatusRef.current = pollTaskStatus;
    startLogPollingRef.current = startLogPolling;
  }, [pollTaskStatus, startLogPolling]);

  // ── Send message ─────────────────────────────────────────────────

  // `overrideText` lets the plan-review card send programmatic messages
  // ("go" on approve, or the composed change description) through the SAME
  // path as a typed message — budget, polling and error handling included.
  const handleSend = useCallback(async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    // Block a new send while a query is running locally OR still in flight server-side (the latter
    // covers a resumed/stuck run where isLoading was cleared but the task is still churning). A
    // plan-approval/clarification continuation passes overrideText and is exempt (always allowed).
    if (!text || isLoading) return;
    if (overrideText === undefined && queriesInFlight > 0) return;
    if (overrideText === undefined) setApprovedPlanReview(null);
    // Budget gate: the cap only blocks STARTING a new slot — continuations of
    // an in-flight slot (answering a clarification, approving/editing a plan)
    // are always allowed by the server. Blocking them here dead-ends the user
    // on their last query with an actionable card that silently no-ops.
    if (queriesRemaining <= 0 && queriesInFlight === 0) {
      setSendError("You've used all your chat queries for this study.");
      return;
    }
    const sessionId = activeSessionId;
    if (!sessionId) return; // bootstrapped on mount via /chat/start

    setSendError(null);
    // Spend one query against the server-enforced budget (403 when out).
    try {
      const consumed = await consumeChatQuery(studyId, text, sessionId);
      setQueriesRemaining(consumed.queries_remaining);
      setQueriesInFlight(consumed.queries_in_flight ?? 0);
    } catch (e) {
      // Surface inline (an active session exists, so startError won't render);
      // re-sync the true budget in case the counter was stale.
      setSendError(e instanceof Error ? e.message : "Couldn't send your question. Please try again.");
      try { const ctx = await getChatContext(studyId); setQueriesRemaining(ctx.queries_remaining); setQueriesInFlight(ctx.queries_in_flight ?? 0); } catch { /* keep counter */ }
      return;
    }

    const userMsg: ChatMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    if (overrideText === undefined) setInput(''); // keep any typed draft on card-driven sends
    // Scroll to bottom after adding user message
    setTimeout(scrollToBottom, 50);
    setIsLoading(true);
    setProgressMessage('Sending query...');
    // Initialize with a minimal structured progress so the thinking bubble shows immediately
    setStructuredProgress({
      current_stage: 'sending',
      message: 'Sending query...',
      plan_steps: [],
    });
    setStepStartTimes({});
    setStageStartTimes({ sending: Date.now() }); // Start tracking sending stage immediately
    setOverallPct(null); // New turn: reset the monotonic header % (#64)
    setQueryStartTime(Date.now()); // Start the elapsed-time indicator (#28/#62)
    progressSnapshotRef.current = null; // Clear any previous snapshot

    try {
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${sessionId}/chat`,
        {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: text,
            context_filtering_enabled: true,
          }),
        },
      );

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          // Marketplace accounts can't use the platform /auth login — surface an
          // inline prompt instead of bouncing them there.
          setSendError('Your session expired. Please refresh the page and sign in again.');
          setIsLoading(false);
          setStructuredProgress(null);
          return;
        }
        throw new Error('Failed to send message');
      }

      const data = await res.json();
      const taskId = data.task_id;
      if (!taskId) throw new Error('No task ID received');

      // Poll with backoff: 1s while fresh, easing off for long tasks (#80)
      startTaskPolling(taskId);
      startLogPolling(sessionId!);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
      }]);
      setIsLoading(false);
      setStructuredProgress(null);
      setStageStartTimes({});
      setStepStartTimes({});
      setOverallPct(null);
      setQueryStartTime(null);
      progressSnapshotRef.current = null; // Clear the ref
    }
  }, [input, isLoading, activeSessionId, navigate, startTaskPolling, startLogPolling, queriesRemaining, queriesInFlight, studyId]);

  // ── Plan review (format 'plan_review') ────────────────────────────
  // Approve = run the plan exactly as shown; edits = a normal chat message
  // carrying the composed change description (the backend resumes/replans).
  // Plan-review ETA pinning: the run banner shows the approved plan's ETA only
  // for runs actually started from the card (see handlePlanApprove).
  const [approvedPlanReview, setApprovedPlanReview] = useState<PlanReviewPayload | null>(null);
  const lastPlanReviewRef = useRef<PlanReviewPayload | null>(null);

  const handlePlanApprove = useCallback(() => {
    // Pin the approved plan's ETA to THIS run — lastAssistantMsg.plan_review
    // would show a stale estimate if the user typed a new question instead.
    setApprovedPlanReview(lastPlanReviewRef.current);
    void handleSend(APPROVE_PLAN_MESSAGE);
  }, [handleSend]);
  const handlePlanRequestChanges = useCallback((message: string) => {
    setApprovedPlanReview(null);
    void handleSend(message);
  }, [handleSend]);
  const focusComposer = useCallback(() => { inputRef.current?.focus(); }, []);

  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);

  const handleCopyMessage = useCallback(async (content: string, messageIndex: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageIndex(messageIndex);
      setTimeout(() => setCopiedMessageIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setSendError('Copy failed — your browser blocked clipboard access. Select and copy the text manually.');
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // ── Render ───────────────────────────────────────────────────────

  // "182 Twins · Multiple profiles" — the SAME rule as the studies list. Joining every
  // cohort produced a line long enough to wrap under the query counter and collide
  // with it. The full list is still on the study's review.
  const audienceContext = [
    twinCount != null && twinCount > 0 ? `${twinCount} Twin${twinCount === 1 ? '' : 's'}` : null,
    audienceSummary(cohortLabels),
  ].filter(Boolean).join(' · ');

  // Plan-derived ETA: once a reviewed plan is running, its own estimate replaces
  // the generic 10–15 min copy. Prefer a live structured_progress ETA; else the
  // plan the user just approved (the plan card is the latest assistant message
  // exactly when the current run was started from it); else the generic copy.
  const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant');
  const runEta = resolveRunEta(structuredProgress, approvedPlanReview ?? undefined);
  lastPlanReviewRef.current = lastAssistantMsg?.plan_review ?? null;
  // elapsedSeconds already ticks here, so pass it through: past ~1.5× the estimate the copy
  // flips to the overrun line instead of sitting on "Estimated 6–8 min · 24m elapsed".
  const runEtaCopy = planEtaRunCopy(runEta?.eta_minutes_min, runEta?.eta_minutes_max, elapsedSeconds * 1000) ?? CHAT_ETA_COPY;

  if (startError && !activeSessionId) {
    return <MarketplaceErrorScreen {...errorScreenPropsFor(startError)} />;
  }

  return (
    <MarketplaceShell maxWidth="max-w-5xl">
      {/* min-w-0 + truncate on the left, flex-shrink-0 + nowrap on the right: without
          both, a long audience line pushed into the counter and the counter wrapped
          mid-phrase ("left" alone on a second line), the two overlapping. */}
      <div className="mb-3 flex flex-col gap-1 md:flex-row md:items-start md:justify-between md:gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-[22px] font-medium text-[#111]">{labelForUseCase('chat')}</h1>
          {/* Wraps on a phone: truncating cut the cohort name ("Dairy Products ›
              …"), so the user could not see WHICH cohort they were talking to. The
              desktop line has room, and keeps truncating rather than reflowing. */}
          <p className="text-[13px] text-[#6b6b6b] md:truncate" title={audienceContext || undefined}>
            Always-on focus group
            {audienceContext && <span className="text-[#999]"> · {audienceContext}</span>}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-3 md:self-start">
          {/* Share the chat — same flow as the Ask Natural/Organic chat, for consistency.
              Visible once there's an active session with messages worth sharing. */}
          {activeSessionId && messages.length > 0 && (
            <button
              onClick={handleShareClick}
              disabled={isCreatingShare}
              className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-[#FAFBFC] px-3 py-1.5 text-xs font-medium text-[#374151] transition-colors hover:border-[#07E5D1] hover:text-[#07E5D1] disabled:opacity-50"
              title="Share this chat"
            >
              {isCreatingShare ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Share2 className="h-3.5 w-3.5" />
              )}
              Share
            </button>
          )}
          <span className="whitespace-nowrap text-[13px] font-medium text-[#111]">
            {queriesRemaining} of {queriesAllowed} queries left
            {queriesInFlight > 0 && (
              <span className="ml-1 font-normal text-[#6b6b6b]">· {queriesInFlight} in progress</span>
            )}
          </span>
        </div>
      </div>
      {/* 72vh is comfortable next to desktop chrome; on a phone the chat IS the page, so
          give it the viewport minus the header and the composer. */}
      <div className="flex overflow-hidden rounded-xl border border-black/10 bg-white h-[70vh] md:h-[72vh]">
        <section className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div ref={chatScrollRef} onScroll={handleChatScroll} className="flex-1 overflow-y-auto">
            <div className="mx-auto max-w-[960px] px-4 py-4 md:px-6 md:py-6">
              {/* Session loading state */}
              {isSessionLoading && (
                <div className="flex items-center justify-center py-16">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 text-[#07E5D1] animate-spin" />
                    <p className="text-sm text-[#6B7280]">Loading conversation...</p>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {!isSessionLoading && messages.length === 0 && !isLoading && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-full bg-[#07E5D1] flex items-center justify-center text-white mx-auto mb-5">
                    <Leaf className="w-8 h-8" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1C1C1E] mb-2">Ask your AI Consumer Twins</h2>
                  <p className="text-sm text-[#6B7280] max-w-md mx-auto mb-6">
                    Ask any question — your always-on focus group will weigh in. {CHAT_UPFRONT_ETA_COPY}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                {([
                  'What drives breakfast purchase decisions?',
                  'How do shoppers choose between breakfast brands?',
                  'What matters most for morning routines?',
                ]).map((s, i) => (
                  <button
                    key={i}
                        onClick={() => setInput(s)}
                        className="px-3.5 py-2 bg-white border border-[#E5E7EB] rounded-xl text-xs text-[#6B7280] hover:border-[#07E5D1] hover:text-[#07E5D1] transition-colors"
                  >
                        {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
              {!isSessionLoading && (
              <div className="space-y-5">
            {messages.map((msg, idx) => (
              <React.Fragment key={idx}>
                {/* Progress accordion shown BEFORE assistant message (between query and response) */}
                {msg.role === 'assistant' && msg.progress_snapshot && (
                  <div className="flex justify-start">
                    <div className="max-w-2xl w-full">
                      <ProgressAccordion progress={msg.progress_snapshot} />
                    </div>
                  </div>
                )}
                {/* Message bubble */}
                <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && msg.format === PLAN_REVIEW_FORMAT && (msg.plan_review?.steps?.length ?? 0) > 0 ? (
                    // The plan card replaces the bubble (msg.content is a markdown
                    // fallback of the same plan). Only the LATEST message is
                    // actionable; older cards render read-only — superseded plans.
                    <PlanReviewCard
                      steps={msg.plan_review!.steps}
                      etaMinutesMin={msg.plan_review!.eta_minutes_min}
                      etaMinutesMax={msg.plan_review!.eta_minutes_max}
                      onApprove={handlePlanApprove}
                      onRequestChanges={handlePlanRequestChanges}
                      onFocusComposer={focusComposer}
                      disabled={isLoading || idx !== messages.length - 1}
                    />
                  ) : (
                  <div
                    className={`rounded-2xl px-5 py-4 ${
                      msg.role === 'user'
                        ? 'max-w-[85%] bg-[#07E5D1] text-white rounded-br-md'
                        // The assistant bubble must have a DEFINITE width. Without
                        // `w-full` it shrink-wraps to its content, and the visual-output
                        // iframe inside (width:100%) then resolves against a parent that
                        // is itself sizing to the iframe — a circular constraint that
                        // collapses to the 300px HTML default, squeezing the whole
                        // insight card into a narrow column. `min-w-0` lets it shrink
                        // inside the flex row instead of overflowing.
                        : 'w-full min-w-0 max-w-full bg-white border border-black/10 text-[#374151] rounded-bl-md'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <div className="w-full min-w-0 max-w-none">
                        {hasRenderableVisual(msg.visual) ? (
                          <InsightToggleView content={msg.content} visual={msg.visual} />
                        ) : (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{msg.content}</ReactMarkdown>
                        )}
                        <div className="flex justify-end mt-3 pt-2 border-t border-[#F1F5F9]">
                          <button
                            onClick={() => handleCopyMessage(msg.content, idx)}
                            // Touch-sized on a phone (was a 65x24 target); the desktop
                            // padding is restored at md so the row stays tight there.
                            className={`flex items-center gap-1.5 min-h-[44px] px-3 py-2 text-xs transition-colors rounded md:min-h-0 md:px-2 md:py-1 ${
                              copiedMessageIndex === idx
                                ? 'text-[#10B981] bg-[#ECFDF5]'
                                : 'text-[#9CA3AF] hover:text-[#07E5D1] hover:bg-[#FAFBFC]'
                            }`}
                            title="Copy to clipboard"
                          >
                            {copiedMessageIndex === idx ? (
                              <>
                                <Check className="w-3.5 h-3.5" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-3.5 h-3.5" />
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                    )}
                  </div>
                  )}
                </div>
              </React.Fragment>
            ))}

                {/* Loading with structured progress */}
                {isLoading && (
              <div className="flex justify-start">
                {structuredProgress ? (
                  <div className="bg-white border border-black/10 rounded-2xl rounded-bl-md w-full max-w-2xl overflow-hidden">
                    {/* Header - Flickering dot with execution status */}
                    <div className="px-5 py-3 border-b border-black/10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-2 h-2 bg-[#07E5D1] rounded-full animate-ping flex-shrink-0" />
                          <p className="text-sm text-gray-700 font-medium truncate">
                            {(() => {
                              const steps = structuredProgress.plan_steps || [];
                              const running = steps.filter(s => s.status === 'running').length;
                              const total = steps.length;

                              // Unambiguous running/total wording (#63)
                              if (running > 0) {
                                return `Executing ${running} of ${total} task${total === 1 ? '' : 's'}${animatedDots}`;
                              }
                              // Every planned step is done but the turn is still running:
                              // we are in the (silent) synthesis phase. Say so, rather than
                              // repeating a stale backend message next to a frozen 95%.
                              if (total > 0 && executionLatched) {
                                return `${CHAT_WRITING_COPY}${animatedDots}`;
                              }
                              // All steps completed or no steps - show backend message
                              return structuredProgress.message || 'Processing...';
                            })()}
                          </p>
                        </div>
                        {/* Percentage fixed on right — monotonic across re-plans (#64),
                            capped at 95% until the task actually completes */}
                        {overallPct != null && phaseForStage(structuredProgress.current_stage) === 'execution' && (
                          <span className="text-sm font-semibold text-[#07E5D1] ml-3 flex-shrink-0">{overallPct}%</span>
                        )}
                      </div>
                      {/* Post-execution phases (generating → validating → visual) each own a
                          % band + ETA, so the bar keeps moving instead of parking at ~95%. */}
                      {phaseForStage(structuredProgress.current_stage) !== 'execution' && (
                        <PhasedProgressBar
                          key={phaseForStage(structuredProgress.current_stage)}
                          stage={structuredProgress.current_stage}
                          isCompleted={false}
                        />
                      )}
                      {/* Once the plan is running (execution stage), set an honest
                          ~10-15 min expectation and tell the user they can leave (#28/#62).
                          Before that (analyzing/planning) just show a short preparing line. */}
                      {(() => {
                        // Latched: a backend re-plan clears plan_steps, and reading it
                        // directly made this flip back to the "preparing" copy mid-run (#64).
                        const executionStarted = executionLatched
                          || (structuredProgress.plan_steps?.length ?? 0) > 0;
                        if (!executionStarted) {
                          return (
                            <p className="text-[11px] text-gray-400 mt-1">
                              {CHAT_PREPARING_COPY}
                              {elapsedSeconds > 0 && ` · ${formatElapsed(elapsedSeconds)} elapsed`}
                            </p>
                          );
                        }
                        // Every planned step is done but the turn is still running: we
                        // are in the silent synthesis phase, which parked the bar at 95%
                        // / "4/4 done" for ~4.5 min and read as HUNG. Name it.
                        const steps = structuredProgress.plan_steps || [];
                        const writing = steps.length > 0
                          && steps.filter((st) => st.status === 'running').length === 0;
                        return (
                          <div className="mt-2 rounded-lg bg-[#E8FCFA] px-3 py-2">
                            <p className="text-[12px] font-medium text-[#0B7C74]">
                              {runEtaCopy}
                              {elapsedSeconds > 0 && ` · ${formatElapsed(elapsedSeconds)} elapsed`}
                            </p>
                            <p className="mt-0.5 text-[11px] leading-snug text-[#0B7C74]/80">
                              {writing ? CHAT_WRITING_HINT : CHAT_LEAVE_HINT}
                            </p>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Executing Plan - only section with progress bars */}
                    {(() => {
                      const planSteps = structuredProgress.plan_steps || [];
                      const executingComplete = planSteps.length > 0 && planSteps.every(s => s.status === 'completed' || s.status === 'skipped');
                      const completedSteps = planSteps.filter(s => s.status === 'completed' || s.status === 'skipped').length;

                      if (planSteps.length === 0) return null;

                      return (
                        <div className="px-5 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Executing Plan
                              </span>
                              {executingComplete && (
                                <CheckCircle2 className="w-3 h-3 text-[#07E5D1]" />
                              )}
                            </div>
                            <span className="text-xs text-gray-400">
                              {completedSteps}/{planSteps.length} done
                            </span>
                          </div>
                          <ul className="space-y-2 ml-1">
                            {planSteps.map((step, idx) => (
                              <li
                                key={`${step.id}-${idx}`}
                                className={`transition-all duration-300 ${
                                  step.status === 'running'
                                    ? 'bg-[#E8FCFA] rounded-lg p-2.5 -mx-1.5 border border-[#07E5D1]/20'
                                    : 'px-0.5'
                                }`}
                              >
                                <div className="flex items-start gap-2.5">
                                  <div className="flex-shrink-0 mt-0.5">
                                    {step.status === 'completed' && (
                                      <CheckCircle2 className="w-4 h-4 text-[#07E5D1]" />
                                    )}
                                    {step.status === 'running' && (
                                      <div className="relative">
                                        <Circle className="w-4 h-4 text-[#07E5D1]" />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <div className="w-1.5 h-1.5 bg-[#07E5D1] rounded-full animate-ping" />
                                        </div>
                                      </div>
                                    )}
                                    {(step.status === 'pending' || step.status === 'skipped') && (
                                      <Circle className="w-4 h-4 text-gray-300" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className={`text-sm leading-tight block ${
                                      step.status === 'completed'
                                        ? 'text-gray-500 line-through'
                                        : step.status === 'running'
                                        ? 'text-gray-800 font-medium'
                                        : 'text-gray-500'
                                    }`}>
                                      {step.label}
                                    </span>
                                    {step.status === 'running' && (
                                      <TimeBasedProgressBar
                                        taskId={step.id}
                                        startTime={stepStartTimes[step.id]}
                                        isCompleted={false}
                                      />
                                    )}
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                    <div className="bg-white border border-black/10 rounded-2xl rounded-bl-md px-5 py-4">
                      <div className="flex items-center gap-3 mb-1">
                        <Loader2 className="w-4 h-4 animate-spin text-[#07E5D1]" />
                        <span className="text-sm font-medium text-[#374151]">{progressMessage}</span>
                  </div>
                      <p className="text-xs text-[#9CA3AF] ml-7">
                        {CHAT_PREPARING_COPY}
                        {elapsedSeconds > 0 && ` · ${formatElapsed(elapsedSeconds)} elapsed`}
                      </p>
                </div>
                )}
              </div>
            )}

                <div className="h-1" />
          </div>
              )}
        </div>
      </div>

          {/* Input */}
          <div className="bg-white border-t border-[#F1F5F9] px-6 py-3">
            {sendError && (
              <div className="max-w-[960px] mx-auto mb-2 text-sm text-[#EF4444]" role="alert">{sendError}</div>
            )}
            {/* Only claim the budget is spent once the session has loaded — before
                /chat/start resolves, queriesRemaining is still its initial 0. */}
            {!isSessionLoading && activeSessionId && queriesRemaining <= 0 ? (
              <div className="max-w-[960px] mx-auto text-center text-sm text-[#6B7280] py-2" role="status">
                You've used all your chat queries for this study.
              </div>
            ) : (
            <div className="max-w-[960px] mx-auto flex items-center gap-3">
            <label htmlFor="mkt-twin-chat-input" className="sr-only">Ask your Twins a question</label>
            <input
              id="mkt-twin-chat-input"
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isLoading || queriesInFlight > 0 ? "Answering… please wait" : "Ask anything…"}
              aria-label="Ask your Twins a question"
              disabled={isLoading || queriesInFlight > 0 || isSessionLoading || !activeSessionId}
                className="flex-1 px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#FAFBFC] text-sm focus:outline-none focus:ring-2 focus:ring-[#07E5D1]/20 focus:border-[#07E5D1] focus:bg-white transition-all disabled:bg-gray-100 disabled:text-[#9CA3AF]"
            />
            <button
              onClick={() => handleSend()}
              aria-label="Send"
              disabled={!input.trim() || isLoading || queriesInFlight > 0 || isSessionLoading || !activeSessionId}
                className="p-3 bg-[#07E5D1] hover:bg-[#06D4C2] text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading || queriesInFlight > 0 ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
            </div>
            )}
          </div>
        </section>
      </div>

      {/* Share modal — mirrors the Ask Natural/Organic chat. */}
      {shareData && (
        <ChatShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          shareUrl={shareData.share_url}
          messageCount={shareData.message_count}
          viewCount={shareData.view_count}
          forkCount={shareData.fork_count}
          onRevoke={handleRevokeShare}
          isRevoking={isRevokingShare}
        />
      )}
    </MarketplaceShell>
  );
};

export default MarketplaceTwinChat;
